import {
    supported
} from '@mapbox/mapbox-gl-supported';

import {
    Map as MapLibre,
    addProtocol,
    LngLatBounds,
    NavigationControl,
    FullscreenControl,
    ScaleControl,
    Marker,
    Popup
} from 'maplibre-gl';

import {
    isMapboxURL,
    transformMapboxUrl
} from 'maplibregl-mapbox-request-transformer';

import mlcontour from 'maplibre-contour';
import {Protocol as PMTilesProtocol} from 'pmtiles';

import I18n from '@js/modules/translations';
import * as Utils from '@js/modules/utils';

import {MAP_MODULES} from '@js/modules/maps/mapConstants';
import MapTileService from '@js/modules/maps/mapTileService';
import Terrain3DControl from '@js/modules/maps/3dButton';

import StorageService from '@js/modules/storage';
import AnalyticsService from '@js/modules/analyticsService';

import Notification from '@js/components/layouts/notification';

import markerIcon from '@/images/maps/marker-icon.png';
import markerArrowIcon from '@/images/maps/marker-arrow.png';
import markerStopIcon from '@/images/maps/marker-stop-play.png';
import markerRunningIcon from '@/images/maps/marker-running-primary.png';
import markerHikingIcon from '@/images/maps/marker-hiking-primary.png';
import markerBikeIcon from '@/images/maps/marker-bike-primary.png';
import markerWalkIcon from '@/images/maps/marker-walk-primary.png';
import markerBlankIcon from '@/images/maps/ride_blank_marker.png';


export default class MapInterface {
    static type = {
        COMPUTE_DISTANCE: 1,
        RIDE: 2,
        SELECTED: 3,
        PLAN: 4,
        IMPORT: 5,
        ALL: 6
    };

    static mode = {
        EXPLORE: 1,
        SHOW: 2,
        CHOROPLETH: 3,
        ASSOCIATION: 4
    };

    static routingService = {
        MAP_PLANNER: 'map_planner',
        MAPBOX: 'mapbox'
    };

    static mapMinZoom = 2;

    // Map center and bbox
    static europeMaxBounds = [
        [-34.49296, 29.735139], // Southwest coordinates (lng, lat)
        [46.75348, 81.47299] // Northeast coordinates (lng, lat)
    ];

    // static europeMinZoom = 5;

    static northAmericaMaxBounds = [
        [-168.697139, 23.857636], // Southwest coordinates (lng, lat)
        [-44.368171, 74.329834] // Northeast coordinates (lng, lat)
    ];

    // static northAmericaMinZoom = 5;

    static userZoom = 8;

    static COORDINATES_PRECISION = 6;

    static DEG_TO_RAD = 0.0174532925199433;

    static EARTH_RADIUS = 6370.997;

    static PointInfo = function (latLng, keyPoint, pointData) {
        pointData ||= {};

        this.keyPoint = keyPoint;
        this.latLng = latLng;

        this.computed = pointData.computed;

        this.deltaDistance = pointData.deltaDistance || 0;
        this.deltaTime = pointData.deltaTime || 0;
        this.deltaPause = pointData.deltaPause || 0;
        this.datetime = pointData.datetime;
        this.elevation = pointData.elevation;
        this.speed = pointData.speed;
        this.heartRate = pointData.heartRate;
        this.surface = pointData.surface;
    };

    static MarkerInfo = function (latLng, type, data) {
        this.latLng = latLng;
        this.type = type;
        this.data = data;
    };

    static MarkerPoint = function (marker, markerId, popup) {
        this.marker = marker;
        this.markerId = markerId;
        this.popup = popup;
    };

    static DirectionArrow = function (keyPoint, linePointIndex) {
        this.keyPoint = keyPoint;
        this.linePointIndex = linePointIndex;
    };

    static SURFACE_GROUPS = {
        paved: {
            surfaces: ['asphalt', 'concrete', 'paved', 'paving_stones', 'cobblestone', 'sett'],
            color: '#616161'
        },
        compacted: {
            surfaces: ['compacted', 'fine_gravel'],
            color: '#FF9800'
        },
        gravel: {
            surfaces: ['gravel'],
            color: '#FFC107'
        },
        dirt: {
            surfaces: ['dirt', 'earth', 'ground', 'mud', 'sand'],
            color: '#8D6E63'
        },
        natural: {
            surfaces: ['grass', 'grass_paver', 'wood'],
            color: '#66BB6A'
        },
        unknown: {
            surfaces: [],
            color: '#9E9E9E'
        }
    };

    static ROAD_CLASS_TO_SURFACE = {
        motorway: 'asphalt',
        trunk: 'asphalt',
        primary: 'asphalt',
        secondary: 'asphalt',
        tertiary: 'asphalt',
        residential: 'asphalt',
        unclassified: 'compacted',
        service: 'asphalt',
        track: 'gravel',
        path: 'dirt',
        footway: 'dirt',
        cycleway: 'asphalt',
        bridleway: 'dirt',
        steps: 'paved',
        living_street: 'asphalt',
        pedestrian: 'paved'
    };

    static surfaceFromRoadClass(roadClass) {
        return MapInterface.ROAD_CLASS_TO_SURFACE[roadClass] || null;
    }

    static getSurfaceGroup(surfaceType) {
        if (!surfaceType) return 'unknown';
        for (const [group, config] of Object.entries(MapInterface.SURFACE_GROUPS)) {
            if (config.surfaces.includes(surfaceType)) return group;
        }
        return 'unknown';
    }

    _mapAvailableModules = [];

    _model = {
        // container for map
        mapId: undefined,
        $mapId: undefined,

        // Map data
        map: undefined,
        mapStyle: undefined,
        mapStyleName: undefined,
        mapDemSource: undefined,
        mapSources: [],
        mapLayers: [],
        openPopups: [],
        mapCustomMarkers: [],
        mapTrackLineColor: '#327d1c',
        mapTrackOutlineColor: '#5de372',
        defaultFlyDuration: 1800,
        isLongTouching: false,
        // Name of the source track line
        mapTrackLineName: 'track-line',
        mapSecondaryLineProps: {
            opacity: 0.5
        },

        isMapLoaded: false,
        isStyleLoaded: false,
        isSourceLoaded: false,
        mapLoadedCallbacks: [],

        isDoubleClick: false,

        clusterMode: false,

        // Elevation map
        elevationMap: undefined,

        // Add map usage limitations
        hasMapLimitations: true,
        mapMaxRasterTilesCount: undefined,
        mapMaxVectorTilesCount: undefined,
        mapMaxRouteComputationCount: undefined,
        mapMaxIsochrone: undefined,
        mapMaxDistance: undefined,

        // params for creation
        initialLocation: undefined,
        initialZoom: undefined,

        // Default values if no points
        defaultLatitude: 47,
        defaultLongitude: 2,

        // Default zoom to display the ride or when displaying the map
        defaultZoom: undefined,
        rideZoom: 11,
        searchZoom: 14,

        // params for creation
        initialLat: undefined,
        initialLong: undefined,

        // bounds provide by user
        mapBounds: null,
        // Reset bounds for a new request
        resetBounds: false,
        // // Initial zoom after fit bounds
        // initZoom: true,
        // Add to bounds
        includeBounds: null,
        // Detect if zoom comes from fit founds
        isAutoZoom: false,
        // Min zoom after fit bounds
        minZoom: undefined,

        // save map bounds when dragging map for next display
        reusePosition: false,

        // Search marker
        searchMarker: undefined,

        // Activate gesture functionalities
        gestureEnabled: false,

        // km or mi
        distanceUnits: window.unit === 'm' ? 'km' : window.unit,

        // Minimum zoom to display tracks lines
        zoomPolyline: window.settings.min_zoom_polyline,

        // Containers for map buttons
        $styleButtonContainers: undefined,

        // Storage for bounds, for each user if possible
        storage: '',

        // Save previous user zoom
        previousZoom: undefined,

        // snapToRoads
        snapToRoads: true,
        isComputingRoad: false,
        previousRoadComputedPoints: 0,
        routingService: null,

        // Routing
        routingRouteType: 'hike',
        // Set when computing the first point (changed to Mapbox if unavailable)
        currentRoutingService: undefined,
        computingRoadCallback: undefined,

        // Object of {distance: , {lat:, lng:}} enumerated by distance traveled (used by highCharts for hover values)
        // Distance are in km or miles
        pointsByDistanceTraveled: {},

        // Filter requests when fetching
        filter: {},

        // Product
        showProductModal: undefined
    };

    alertPopup = undefined;

    constructor(modules, params = {}) {
        this._mapAvailableModules = modules;

        this._model.hasMapLimitations = params.hasMapLimitations;
        this._model.mapMaxRasterTilesCount = params.mapMaxRasterTilesCount;
        this._model.mapMaxVectorTilesCount = params.mapMaxVectorTilesCount;

        this._model.mapMaxDistance = params.mapMaxDistance;
        this._model.mapMaxRouteComputationCount = params.mapMaxRouteComputationCount;
        this._model.mapMaxIsochrone = params.mapMaxIsochrone;

        this._model.mapCustomMarkers = params.mapMarkers || [];

        this._model.clusterMode = params.clusterMode;

        this._model.initialLocation = params.initialLocation;
        this._model.initialLat = params.initialLat;
        this._model.initialLong = params.initialLong;

        this._model.initialZoom = params.initialZoom;
        this._model.defaultZoom = params.defaultZoom;
        this._model.minZoom = params.minZoom;

        if (Utils.isPresent(params.reusePosition)) {
            this._model.reusePosition = params.reusePosition;
        }

        if (Utils.isPresent(params.resetBounds)) {
            this._model.resetBounds = params.resetBounds;
        }

        if (Utils.isPresent(params.includeBounds)) {
            this._model.includeBounds = params.includeBounds;
        }

        if (Utils.isPresent(params.snapToRoads)) {
            this._model.snapToRoads = params.snapToRoads;
        }

        if (params.distanceUnits) {
            this._model.distanceUnits = params.distanceUnits;
        }

        this._model.mapId = params.mapId;
        this._model.$mapId = document.getElementById(params.mapId);

        if (Utils.isPresent(params.filter)) {
            this._model.filter = params.filter;
        }

        this._model.showProductModal = params.showProductModal;

        if (Utils.isEmpty(this._model.mapId)) {
            throw new Error('Set a valid map id: ' + this._model.mapId);
        }

        if (!this._model.$mapId) {
            throw new Error('Div Map container not found, cannot display map: #' + this._model.mapId);
        }

        if (this.hasModule('GEOLOCATION') && this.plugGeolocator) {
            this.plugGeolocator({
                mapId: params.mapId,
                isTracking: params.isGeoTracking,
                showRotateMap: params.showRotateMap
            });
        }

        // Create map
        const mapPosition = this._getCurrentMapPosition();
        this._createMap({
            mapId: this._model.mapId + '-map',
            boundingBox: mapPosition.boundingBox,
            center: mapPosition.center,
            zoom: mapPosition.zoom,
            hideMapScale: params.hideMapScale,
            gestureEnabled: this._model.gestureEnabled,
            mapLimitations: this._model.hasMapLimitations ? {
                mapMaxRasterTilesCount: this._model.mapMaxRasterTilesCount,
                mapMaxVectorTilesCount: this._model.mapMaxVectorTilesCount
            } : false,
            geolocator: this.hasModule('GEOLOCATION') && this._geolocationModel ? this._geolocationModel.geolocator : undefined
        });

        if (!this._model.map) {
            return;
        }

        // Change zoom speed for mouse wheel: default is 1/450
        this._model.map.scrollZoom.setWheelZoomRate(1 / 250);
        // Change zoom speed for trackpad: default is 1/100
        this._model.map.scrollZoom.setWheelZoomRate(1 / 250);

        MapTileService.showProductModal = this._model.showProductModal;

        // Wait for map to load to do stuff (mapbox can be finicky like that)
        this._model.map.once('load', () => {
            this._initializeSourcesAndLayers(() => {
                if (this.hasModule('GEOLOCATION') && this.plugGeolocatorButtons) {
                    this.plugGeolocatorButtons();
                }

                this._mapLoaded({
                    mapLoaded: true
                });
            }, params);
        });

        Utils.onPageReady(() => {
            import(/* webpackChunkName: "theme-swal" */ '@js/modules/swal')
                .then((sweetalert2) => {
                    this.alertPopup = sweetalert2.default;
                });
        }, 2600);
    }

    onMapLoaded = (callbackFunction) => {
        if (this._model.isMapLoaded) {
            callbackFunction();
        } else {
            this._model.mapLoadedCallbacks.push(callbackFunction);
        }
    };

    hasModule = (moduleName) => {
        if (!this._mapAvailableModules || !this._mapAvailableModules.length) {
            return false;
        }

        if (!Object.values(MAP_MODULES)
            .includes(moduleName)) {
            throw `Unknown module to load: ${moduleName}`;
        }

        return this._mapAvailableModules.includes(moduleName);
    };

    setSnapToRoad = (value) => {
        this._model.snapToRoads = value;
    };

    setRouteType = (routeType) => {
        this._model.routingRouteType = routeType;
    };

    onComputingRoad = (callback) => {
        this._model.computingRoadCallback = callback;
    };

    /* options: */
    /* - zoom: max zoom on fit bounds */
    /* - padding: padding around bounds (0 - max 150) */
    fitBounds = (bounds, options = {}) => {
        if (!this._model.map) {
            return;
        }

        if (!bounds) {
            return null;
        }
        if (!Array.isArray(bounds) && (!bounds._sw || !bounds._ne)) {
            return null;
        }

        const boundOptions = {
            // linear: true
        };
        if (Utils.isPresent(options.zoom)) {
            // maximum zoom level to allow
            boundOptions.maxZoom = options.zoom;
        }
        if (Utils.isPresent(options.padding)) {
            boundOptions.padding = options.padding;
            // There is a maximum padding, above 150, fit bounds not working!
            if (boundOptions.padding > 150) {
                boundOptions.padding = 150;
            }
        }

        if (Array.isArray(bounds)) {
            if (bounds.length === 0) {
                return null;
            } else if (bounds.length === 2) {
                if (!this._isValidCoordinates(bounds[0]) || !this._isValidCoordinates(bounds[1])) {
                    return null;
                }
            } else if (bounds.length === 4) {
                if (!this._isValidCoordinates([bounds[0], bounds[1]]) || !this._isValidCoordinates([bounds[2], bounds[3]])) {
                    return null;
                }
            } else {
                let polygonBounds = this._buildBounds();

                bounds.forEach((point) => {
                    polygonBounds = polygonBounds.extend([point[0], point[1]]);
                });

                bounds = polygonBounds;
            }
        } else if (bounds._sw || bounds._ne) {
            if (!this._isValidCoordinates(bounds._sw) || !this._isValidCoordinates(bounds._ne)) {
                return null;
            }
        }

        return this._model.map.fitBounds(bounds, boundOptions);
    };

    getZoom = () => {
        return this._model.map?.getZoom();
    };

    once = (event, callback) => {
        if (!this._model.map) {
            return;
        }

        this._model.map.once(event, callback);
    };

    flyTo = (latLng, zoom, duration) => {
        if (!this._model.map) {
            return;
        }

        if (this._isMapPlannerStyle()) {
            this._model.map.easeTo({
                center: latLng,
                zoom: zoom || this._model.defaultZoom,
                duration: duration || this._model.defaultFlyDuration
            });
        } else {
            // Display directly the point to avoid too many map loads
            this._model.map.jumpTo({
                center: latLng,
                zoom: zoom || this._model.defaultZoom,
                duration: duration || this._model.defaultFlyDuration
            });
        }
    };

    highlightPlaceName = (lat, lng, name) => {
        if (!name) {
            return;
        }

        if (this._model.map.getSource('search-highlight-source')) {
            this._model.map.removeLayer('search-highlight-layer');
            this._model.map.removeSource('search-highlight-source');
        }

        this._model.map.addSource('search-highlight-source', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    properties: {
                        name: name
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat]
                    }
                }]
            }
        });

        this._model.map.addLayer({
            id: 'search-highlight-layer',
            type: 'symbol',
            source: 'search-highlight-source',
            minzoom: 7,
            layout: {
                'text-field': ['get', 'name'],
                'text-size': 18,
                'text-allow-overlap': true
            },
            paint: {
                'text-color': '#bf2113',
                'text-halo-color': 'rgba(255,255,255,0.8)',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.2
            }
        });
    };

    displaySearchMarker = (lat, lng) => {
        if (this._model.searchMarker) {
            this._model.searchMarker.remove();
        }

        if (!this._isValidCoordinates([lat, lng])) {
            return;
        }

        this._model.searchMarker = this.buildMarker('search-marker', {
            lat,
            lng
        }, {
            clickable: false,
            draggable: false,
            markerImage: markerIcon
        });

        if (lat && lng && this._model.map) {
            this._model.searchMarker.addTo(this._model.map);

            this._model.searchMarker.getElement()
                .addEventListener('click', function (event) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (this._model.searchMarker) {
                        this._model.searchMarker.remove();
                    }
                }.bind(this));

            this._model.searchMarker.getElement()
                .addEventListener('contextmenu', function (event) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (this._model.searchMarker) {
                        const searchCoordinates = this._model.searchMarker.getLngLat();

                        Notification.info(I18n.t('js.maps.marker.share', {url: `${document.location.origin}?marker=true&lat=${searchCoordinates.lat}&lng=${searchCoordinates.lng}`}), false);

                        this._model.searchMarker.remove();
                    }
                }.bind(this));
        }
    };

    toggleLoader = (message) => {
        const $mapArea = this._model.$mapId.querySelector('.map-area');
        const $mapLoader = $mapArea.querySelector('.map-loader');
        if (!$mapLoader) {
            return;
        }
        const $mapLoaderContent = $mapArea.querySelector('.map-loader-content');
        if (!$mapLoader) {
            return;
        }

        if (Utils.isPresent(message)) {
            if ($mapLoaderContent) {
                $mapLoaderContent.textContent = message;
            }
            $mapLoader.style.display = 'block';
        } else {
            $mapLoader.style.display = 'none';
        }
    };

    getZoomDictionary = (zoomLevel) => {
        zoomLevel = Math.floor(zoomLevel);

        if (zoomLevel >= 17) {
            return {
                ghostDist: 20,
                stepDist: 20,
                directionArrow: 200
            };
        } else if (zoomLevel === 16) {
            return {
                ghostDist: 40,
                stepDist: 80,
                directionArrow: 200
            };
        } else if (zoomLevel === 15) {
            return {
                ghostDist: 50,
                stepDist: 200,
                directionArrow: 300
            };
        } else if (zoomLevel === 14) {
            return {
                ghostDist: 80,
                stepDist: 300,
                directionArrow: 500
            };
        } else if (zoomLevel === 13 || zoomLevel === 12) {
            return {
                ghostDist: 100,
                stepDist: 500,
                directionArrow: 750
            };
        } else {
            return {
                ghostDist: null,
                stepDist: 1000,
                directionArrow: null
            };
        }
    };

    isDataChangedOnZoom = (zoomLevel, previousZoomLevel, arrowOnly) => {
        const previousDictionary = this.getZoomDictionary(previousZoomLevel);
        const currentDictionary = this.getZoomDictionary(zoomLevel);

        const ghostDistChanged = previousDictionary.ghostDist !== currentDictionary.ghostDist;
        const stepDistChanged = previousDictionary.stepDist !== currentDictionary.stepDist;
        const directionArrowChanged = previousDictionary.directionArrow !== currentDictionary.directionArrow;

        if (arrowOnly) {
            return directionArrowChanged;
        } else {
            return ghostDistChanged || stepDistChanged || directionArrowChanged;
        }
    };

    distHaversine = (p1, p2, decimal = 6) => {
        const rad = (x) => x * MapInterface.DEG_TO_RAD;

        const lat1 = p1.lat;
        const lng1 = p1.lng;
        const lat2 = p2.lat;
        const lng2 = p2.lng;

        const dLat = rad(lat2 - lat1);
        const dLong = rad(lng2 - lng1);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = MapInterface.EARTH_RADIUS * c;

        // return distance in meters
        return parseFloat((distance * 1000).toFixed(decimal));
    };

    addMapTrackLine = (trackLineSourceId, lineProps = {}) => {
        if (!this._model.map) {
            return;
        }

        if (this._model.map.getSource(trackLineSourceId)) {
            return trackLineSourceId;
        }

        this._model.map.addSource(trackLineSourceId, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });
        this._model.mapSources.push(trackLineSourceId);

        // this._model.map.addLayer({
        //     id: trackLineSourceId,
        //     type: 'line',
        //     source: trackLineSourceId,
        //     layout: {
        //         'line-join': 'round',
        //         'line-cap': 'round'
        //     },
        //     paint: {
        //         'line-color': this._model.mapTrackLineColor,
        //         'line-width': 5,
        //         'line-opacity': 0.75
        //     }
        // });
        // this._model.mapLayers.push(trackLineSourceId);

        // Add line with outline
        [-1, 0, 1, 0].forEach((x, i) => {
            const trackName = trackLineSourceId + '-' + i;

            // Display layer after symbols (use the first symbol id to position this layer below the other layers)
            this._model.map.addLayer({
                id: trackName,
                type: 'line',
                source: trackLineSourceId,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': i === 3
                        ? (lineProps.outlineColor || this._model.mapTrackOutlineColor)
                        : (lineProps.color || this._model.mapTrackLineColor),
                    'line-width': i === 3 ? 5 : 3,
                    'line-offset': x * 3,
                    'line-opacity': lineProps.opacity || 1
                }
            }, this._model.mapLayers.first());

            this._model.mapLayers.push(trackName);
        });

        return trackLineSourceId;
    };

    static SLOPE_COLOR_EXPRESSION = [
        'step', ['get', 'slope'],
        '#4CAF50',
        5, '#FFC107',
        10, '#FF9800',
        15, '#FF5722',
        20, '#F44336'
    ];

    buildSlopeGeoJSON = (coordinateElevations) => {
        const features = [];
        for (let i = 1; i < coordinateElevations.length; i++) {
            const prev = coordinateElevations[i - 1];
            const curr = coordinateElevations[i];
            if (curr.deltaDistance <= 0) continue;
            const slope = Math.abs((curr.elevation - prev.elevation) / curr.deltaDistance * 100);
            features.push({
                type: 'Feature',
                properties: {slope},
                geometry: {
                    type: 'LineString',
                    coordinates: [[prev.lng, prev.lat], [curr.lng, curr.lat]]
                }
            });
        }
        return {type: 'FeatureCollection', features};
    };

    setSlopePercentageMode = (trackLineSourceId, enabled) => {
        if (!this._model.map) {
            return;
        }

        [0, 1, 2, 3].forEach((i) => {
            const layerId = trackLineSourceId + '-' + i;
            if (!this._model.map.getLayer(layerId)) {
                return;
            }

            if (enabled) {
                if (i === 3) {
                    this._model.map.setPaintProperty(layerId, 'line-color', 'rgba(50, 50, 50, 0.25)');
                } else {
                    this._model.map.setPaintProperty(layerId, 'line-color', MapInterface.SLOPE_COLOR_EXPRESSION);
                }
            } else {
                this._model.map.setPaintProperty(
                    layerId,
                    'line-color',
                    i === 3 ? this._model.mapTrackOutlineColor : this._model.mapTrackLineColor
                );
            }
        });
    };

    buildSurfaceGeoJSON = (linePoints) => {
        const features = [];
        for (let i = 1; i < linePoints.length; i++) {
            const prev = linePoints[i - 1];
            const curr = linePoints[i];
            const surfaceGroup = MapInterface.getSurfaceGroup(curr.surface || prev.surface);
            features.push({
                type: 'Feature',
                properties: {surfaceGroup},
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [prev.latLng.lng, prev.latLng.lat],
                        [curr.latLng.lng, curr.latLng.lat]
                    ]
                }
            });
        }
        return {type: 'FeatureCollection', features};
    };

    setSurfaceTypeMode = (trackLineSourceId, enabled, surfaceGeoJSON) => {
        if (!this._model.map) {
            return;
        }

        const surfaceSourceId = trackLineSourceId + '-surface';

        if (enabled) {
            // Hide track layers 0-3
            [0, 1, 2, 3].forEach((i) => {
                const layerId = trackLineSourceId + '-' + i;
                if (this._model.map.getLayer(layerId)) {
                    this._model.map.setLayoutProperty(layerId, 'visibility', 'none');
                }
            });

            // Add or update surface source
            if (this._model.map.getSource(surfaceSourceId)) {
                this._model.map.getSource(surfaceSourceId).setData(surfaceGeoJSON);
            } else {
                this._model.map.addSource(surfaceSourceId, {
                    type: 'geojson',
                    data: surfaceGeoJSON
                });
            }

            // Add outline + main layer per surface group
            for (const [group, config] of Object.entries(MapInterface.SURFACE_GROUPS)) {
                const outlineId = surfaceSourceId + '-' + group + '-outline';
                const mainId = surfaceSourceId + '-' + group;
                const filter = ['==', ['get', 'surfaceGroup'], group];

                // Outline layer (wider, lighter)
                if (!this._model.map.getLayer(outlineId)) {
                    this._model.map.addLayer({
                        id: outlineId,
                        type: 'line',
                        source: surfaceSourceId,
                        filter,
                        layout: {'line-cap': 'round', 'line-join': 'round'},
                        paint: {
                            'line-color': config.color,
                            'line-width': 8,
                            'line-opacity': 0.3
                        }
                    });
                }

                // Main layer
                if (!this._model.map.getLayer(mainId)) {
                    this._model.map.addLayer({
                        id: mainId,
                        type: 'line',
                        source: surfaceSourceId,
                        filter,
                        layout: {'line-cap': 'round', 'line-join': 'round'},
                        paint: {
                            'line-color': config.color,
                            'line-width': 5,
                            'line-opacity': 1
                        }
                    });
                }

                this._model.map.setLayoutProperty(outlineId, 'visibility', 'visible');
                this._model.map.setLayoutProperty(mainId, 'visibility', 'visible');
            }
        } else {
            // Show track layers 0-3
            [0, 1, 2, 3].forEach((i) => {
                const layerId = trackLineSourceId + '-' + i;
                if (this._model.map.getLayer(layerId)) {
                    this._model.map.setLayoutProperty(layerId, 'visibility', 'visible');
                }
            });

            // Hide surface layers
            for (const group of Object.keys(MapInterface.SURFACE_GROUPS)) {
                const outlineId = surfaceSourceId + '-' + group + '-outline';
                const mainId = surfaceSourceId + '-' + group;
                if (this._model.map.getLayer(outlineId)) {
                    this._model.map.setLayoutProperty(outlineId, 'visibility', 'none');
                }
                if (this._model.map.getLayer(mainId)) {
                    this._model.map.setLayoutProperty(mainId, 'visibility', 'none');
                }
            }
        }
    };

    removeSurfaceLayers = () => {
        if (!this._model.map) {
            return;
        }

        const surfaceSourceId = this._model.mapTrackLineName + '-surface';

        for (const group of Object.keys(MapInterface.SURFACE_GROUPS)) {
            const mainId = surfaceSourceId + '-' + group;
            const outlineId = surfaceSourceId + '-' + group + '-outline';
            if (this._model.map.getLayer(mainId)) {
                this._model.map.removeLayer(mainId);
            }
            if (this._model.map.getLayer(outlineId)) {
                this._model.map.removeLayer(outlineId);
            }
        }

        if (this._model.map.getSource(surfaceSourceId)) {
            this._model.map.removeSource(surfaceSourceId);
        }
    };

    updateSource = (sourceId, data) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceId);
        if (!currentSource) {
            return;
        }

        currentSource.setData(data);

        return currentSource;
    };

    hasSourceData = (sourceName) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceName);
        if (!currentSource) {
            return false;
        }

        const sourceData = currentSource.serialize();
        const data = sourceData.data;

        if (!data) {
            return false;
        }

        if (data.type === 'FeatureCollection') {
            return !!data.features &&
                !!data.features.length &&
                data.features[0].geometry.coordinates &&
                data.features[0].geometry.coordinates[0] !== 0 &&
                data.features[0].geometry.coordinates[1] !== 0;
        } else if (data.type === 'Feature' && data.geometry?.type === 'LineString') {
            return !!data.geometry && !!data.geometry.coordinates && data.geometry.coordinates.length > 1;
        } else if (data.type === 'Feature' && data.geometry?.type === 'Point') {
            return !!data.geometry &&
                !!data.geometry.coordinates &&
                data.geometry.coordinates.length > 0 &&
                data.geometry.coordinates[0] !== 0 &&
                data.geometry.coordinates[1] !== 0;
        }

        return false;
    };

    resetMapSource = (sourceName, remove) => {
        if (!this._model.map || !this._model.map.style) {
            return;
        }

        const currentSource = this._model.map.getSource(sourceName);
        if (!currentSource) {
            return;
        }

        if (remove) {
            if (sourceName.includes('track-line')) {
                [0, 1, 2, 3].forEach((index) => {
                    if (this._model.map.getLayer(`${sourceName}-${index}`)) {
                        this._model.map.removeLayer(`${sourceName}-${index}`);
                    }

                    // this._model.mapLayers = this._model.mapLayers.filter((layer) => layer !== `${sourceName}-${index}`);
                });
            }

            if (this._model.map.getLayer(sourceName)) {
                this._model.map.removeLayer(sourceName);

                // this._model.mapLayers = this._model.mapLayers.filter((layer) => layer !== sourceName);
            }

            this._model.map.removeSource(sourceName);
        } else {
            const sourceData = currentSource.serialize();
            const dataType = sourceData.data?.type;

            if (dataType === 'FeatureCollection') {
                currentSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            } else if (dataType === 'Feature') {
                currentSource.setData({
                    type: 'Feature'
                });
            } else {
                throw new Error('Unknown data type to reset: ' + dataType);
            }
        }
    };

    buildMarker = (markerClassName, latLng, markerOptions) => {
        if (!this._isValidCoordinates(latLng)) {
            return;
        }

        const markerElement = document.createElement('div');
        markerElement.className = markerClassName;

        if (markerOptions) {
            if (markerOptions.markerContent) {
                markerElement.innerHTML = markerOptions.markerContent;
            }
            if (markerOptions.markerImage) {
                markerElement.style.backgroundImage = `url('${markerOptions.markerImage}')`;
            }
            if (markerOptions.markerOpacity) {
                markerElement.style.opacity = markerOptions.markerOpacity;
            }
        }

        return new Marker({element: markerElement, ...markerOptions}).setLngLat(latLng);
    };

    buildPopup = (options = {}) => {
        return new Popup({
            ...options,
            closeButton: false,
            closeOnClick: options.closeOnClick ?? true,
            maxWidth: (options.maxWidth || 320) + 'px',
            offset: options.offset || [0, -10]
        });
    };

    closeAllPopups = () => {
        // Close previous marker popups
        if (this._model.openPopups.length) {
            for (let previousPopupIndex = 0; previousPopupIndex < this._model.openPopups.length; previousPopupIndex++) {
                const previousPopup = this._model.openPopups[previousPopupIndex];

                if (previousPopup) {
                    previousPopup.remove();
                }
            }

            this._model.openPopups = [];
        }
    };

    manageMarkerPopup = (markerId, onMarkerClick, onMarkerHover, onMarkerLeave) => {
        // Manage click on marker
        const sourceName = markerId;

        this._model.map.on('click', sourceName, (event) => {
            event.originalEvent.stopPropagation();

            if (onMarkerClick) {
                onMarkerClick(this._convertPopupCoordinates(event), event.features[0].properties);
            }
        });

        // Manage mouse hover on marker
        if (onMarkerHover) {
            this._model.map.on('mouseenter', sourceName, (event) => {
                this._model.map.getCanvas().style.cursor = 'pointer';

                onMarkerHover(this._convertPopupCoordinates(event), event.features[0].properties);
            });
        }

        if (onMarkerLeave) {
            this._model.map.on('mouseleave', sourceName, () => {
                if (!this._model.map) {
                    return;
                }

                this._model.map.getCanvas().style.cursor = '';

                onMarkerLeave();
            });
        }

        // Manage click on marker cluster
        if (this._model.clusterMode) {
            this._model.map.on('click', `${sourceName}-cluster`, (event) => {
                // Manage clusters
                this._manageCluster(markerId, event.point);
            });
        }
    };

    // isHillshadeAvailable = () => {
    //     const currentMapPosition = this._getCurrentMapPosition();
    //
    //     if (!currentMapPosition && !currentMapPosition.center) {
    //         return true;
    //     }
    //
    //     const initialPoint;
    //
    //     if (Array.isArray(currentMapPosition.center)) {
    //         initialPoint = [currentMapPosition.center[0], currentMapPosition.center[1]];
    //     } else {
    //         initialPoint = [currentMapPosition.center.lng, currentMapPosition.center.lat];
    //     }
    //
    //     const europeBounds = new LngLatBounds(MapInterface.europeMaxBounds);
    //     const northAmericaBounds = new LngLatBounds(MapInterface.northAmericaMaxBounds);
    //
    //     return !!(europeBounds.contains(initialPoint) || northAmericaBounds.contains(initialPoint));
    // };

    selectRoutingService = (coordinate) => {
        const firstPoint = [coordinate.lng, coordinate.lat];
        const europeBounds = new LngLatBounds(MapInterface.europeMaxBounds);
        const northAmericaBounds = new LngLatBounds(MapInterface.northAmericaMaxBounds);

        let routingService = MapInterface.routingService.MAP_PLANNER;
        if (!europeBounds.contains(firstPoint) && !northAmericaBounds.contains(firstPoint)) {
            routingService = MapInterface.routingService.MAPBOX;
        }

        return routingService;
    };

    isMapPlannerRouting = (routingService) => {
        return !routingService || routingService === MapInterface.routingService.MAP_PLANNER;
    };

    setMapStyle = (newStyle) => {
        this._handleMapStyles({
            newStyle,
            onNewStyleLoaded: () => {
                this._initializeSourcesAndLayers(() => {
                    if (this._onMapStyleChanged) {
                        this._onMapStyleChanged();
                    }
                });
            }
        });
    };

    onUnitChanged = (newUnit) => {
        if (!this._model.map) {
            return;
        }

        this._model.distanceUnits = newUnit;

        if (this._model.map.getSource('contours')) {
            this._model.map.removeLayer('contour_lines');
            this._model.map.removeLayer('contour_labels');
            this._model.map.removeSource('contours');
        }

        if (this._model.mapStyleName === 'map_planner_outdoor') {
            this._loadContours();
        }

        this._updateUnitOnMap();

        if (this._onMapStyleChanged) {
            this._onMapStyleChanged();
        }
    };

    highlightMapElement = (element) => {
        const elementToLayer = {
            parking: 'poi_parking',
            viewpoint: 'poi_viewpoint',
            drinking_water: 'poi_drinking_water'
        };
        const elementToFilter = {
            parking: [
                'all',
                ['==', '$type', 'Point'],
                ['==', 'class', 'amenity'],
                ['in', 'subclass', 'parking']
            ],
            viewpoint: [
                'all',
                ['==', '$type', 'Point'],
                ['==', 'class', 'outdoor_poi'],
                ['==', 'subclass', 'viewpoint']
            ],
            drinking_water: [
                'all',
                ['==', '$type', 'Point'],
                [
                    'in',
                    'class',
                    'drinking_water',
                    'watering_place',
                    'water_point'
                ]
            ]
        };

        const layerName = elementToLayer[element];

        if (this._model.mapStyleName === MapTileService.layers.MAP_PLANNER || this._model.mapStyleName === MapTileService.layers.MAP_PLANNER_OUTDOOR) {
            if (this._model.map.getLayer(layerName + '-halo')) {
                this._model.map.setLayerZoomRange(layerName, 14, 24);
                this._model.map.setLayoutProperty(layerName, 'icon-size', 0.8);
                this._model.map.setPaintProperty(layerName, 'icon-opacity', 0.9);
                this._model.map.setLayoutProperty(layerName, 'icon-allow-overlap', false);
                this._model.map.setLayoutProperty(layerName, 'icon-ignore-placement', false);

                this._model.map.removeLayer(layerName + '-halo');
            } else {
                this._model.map.setLayerZoomRange(layerName, 10, 24);
                this._model.map.setLayoutProperty(layerName, 'icon-size', 1.6);
                this._model.map.setPaintProperty(layerName, 'icon-opacity', 1);
                this._model.map.setLayoutProperty(layerName, 'icon-allow-overlap', true);
                this._model.map.setLayoutProperty(layerName, 'icon-ignore-placement', true);

                this._model.map.addLayer({
                    id: layerName + '-halo',
                    type: 'circle',
                    source: 'map-planner',
                    'source-layer': 'poi',
                    'filter': elementToFilter[element],
                    paint: {
                        'circle-radius': 30,
                        'circle-color': '#d5d5d5',
                        'circle-opacity': 0.9,
                        'circle-blur': 0.6
                    }
                }, layerName);

                if (this._model.map.getZoom() < 14) {
                    // Notification.info(I18n.t('js.maps.toolbar.highlight.notif'));

                    this._model.map.flyTo({
                        zoom: 14,
                        speed: 1.2,
                        curve: 1.42,
                        essential: true
                    });
                }
            }
        }
    };

    queryNearbyParkings = (center, radiusMeters) => {
        if (!this._model.map) {
            return [];
        }

        const parkingFilter = [
            'all',
            ['==', 'class', 'amenity'],
            ['in', 'subclass', 'parking']
        ];

        const features = this._model.map.querySourceFeatures('map-planner', {
            sourceLayer: 'poi',
            filter: parkingFilter
        });

        const seen = new Set();
        const results = [];

        features.forEach((feature) => {
            const coords = feature.geometry.type === 'MultiPoint'
                ? feature.geometry.coordinates
                : [feature.geometry.coordinates];

            coords.forEach(([lng, lat]) => {
                const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
                if (seen.has(key)) {
                    return;
                }
                seen.add(key);

                const distance = this.distHaversine(center, {lat, lng});
                if (distance > radiusMeters) {
                    return;
                }

                results.push({
                    name: feature.properties.name || feature.properties['name:latin'] || null,
                    lat,
                    lng,
                    distance
                });
            });
        });

        return results.sort((a, b) => a.distance - b.distance);
    };

    changeMapFontSize = (delta) => {
        if (!this._model.map) {
            return;
        }

        // Store original text-size values on first use
        if (!this._model.originalTextSizes) {
            this._model.originalTextSizes = {};
            this._model.mapFontScale = 1;

            const style = this._model.map.getStyle();
            if (!style?.layers) {
                return;
            }

            style.layers.forEach((layer) => {
                if (layer.type === 'symbol' && layer.layout?.['text-size'] !== undefined) {
                    this._model.originalTextSizes[layer.id] = JSON.parse(JSON.stringify(layer.layout['text-size']));
                }
            });
        }

        this._model.mapFontScale = Math.max(0.5, Math.min(2.5, this._model.mapFontScale + delta * 0.1));

        Object.entries(this._model.originalTextSizes).forEach(([layerId, originalSize]) => {
            if (!this._model.map.getLayer(layerId)) {
                return;
            }

            if (typeof originalSize === 'number') {
                this._model.map.setLayoutProperty(layerId, 'text-size', Math.round(originalSize * this._model.mapFontScale));
            } else if (Array.isArray(originalSize)) {
                this._model.map.setLayoutProperty(layerId, 'text-size', ['*', originalSize, this._model.mapFontScale]);
            } else if (originalSize?.stops) {
                // Legacy stops format: {base, stops: [[zoom, size], ...]}
                const scaled = {
                    ...originalSize,
                    stops: originalSize.stops.map(([zoom, size]) => [zoom, Math.round(size * this._model.mapFontScale)])
                };
                this._model.map.setLayoutProperty(layerId, 'text-size', scaled);
            }
        });
    };

    // enterFullscreen = () => {
    //     if (!this._model.map) {
    //         return;
    //     }
    //
    //     if (!document.fullscreenElement && typeof this._model.map.getContainer().requestFullscreen === 'function') {
    //         this._model.map.getContainer()
    //             .requestFullscreen();
    //     }
    // };

    leaveFullscreen = () => {
        if (!this._model.map) {
            return;
        }

        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
            document.exitFullscreen();
        }
    };

    destroy = () => {
        if (!this._model.map) {
            return;
        }

        this._model.map.remove();

        if (this.hasModule('ELEVATION_COMPUTATION') && this.destroyElevationMap) {
            this.destroyElevationMap();
        }
    };

    /* Private Methods
    ******************** */
    _mapLoaded = (params) => {
        if (!this._model.map || this._model.isMapLoaded) {
            for (let i = 0, n = this._model.mapLoadedCallbacks.length; i < n; i++) {
                this._model.mapLoadedCallbacks[i]();
            }
        } else {
            if (params.mapLoaded) {
                this._model.isMapLoaded = true;
            }

            // (this._model.map.loaded() && this._model.map.isStyleLoaded())

            for (let i = 0, n = this._model.mapLoadedCallbacks.length; i < n; i++) {
                this._model.mapLoadedCallbacks[i]();
            }
        }
    };

    _isValidCoordinates = (latLng) => {
        let lat;
        let lng;

        if (latLng && latLng.lat && latLng.lng) {
            lat = latLng.lat;
            lng = latLng.lng;
        } else if (Array.isArray(latLng)) {
            lat = latLng[0];
            lng = latLng[1];
        }

        return (Number.isFinite(lat) && Math.abs(lat) <= 90) && (Number.isFinite(lng) && Math.abs(lng) <= 180);
    };

    _isMapPlannerStyle = () => {
        return this._model.mapStyleName === MapTileService.layers.MAP_PLANNER || this._model.mapStyleName === MapTileService.layers.MAP_PLANNER_OUTDOOR;
    };

    _manageMapProviderLimitation = () => {
        this._model.map.setMaxBounds(null);
        this._model.map.setMinZoom(MapInterface.mapMinZoom);
    };

    _handleMapStyles = (mapStyleOptions) => {
        let mapMaxZoom = 18;

        const isNewStyle = !!mapStyleOptions.newStyle;

        // Select layer service
        this._model.mapStyleName = mapStyleOptions.newStyle || MapTileService.getTileService() || MapTileService.layers.MAP_PLANNER;

        this._model.mapStyle = MapTileService.selectTileStyle(this._model.mapStyleName, {
            isNewStyle: isNewStyle
        });

        if (this._model.mapStyleName === 'mapbox_satellite') {
            mapMaxZoom = 17;
        }

        if (this._model.map) {
            this._model.map.setMaxZoom(mapMaxZoom);
        }

        if (!this._model.mapStyle) {
            throw new Error('MAP: No style declared for map');
        }

        if (isNewStyle && this._model.map) {
            this._model.map.setStyle(this._model.mapStyle);

            this._manageMapProviderLimitation();

            // style.load or style.change not working anymore since Maplibre 3
            if (mapStyleOptions.onNewStyleLoaded) {
                this._model.map.once('styledata', () => {
                    mapStyleOptions.onNewStyleLoaded(this._model.mapStyle);
                });
            }
        }

        return mapMaxZoom;
    };

    _createMap = (mapParams) => {
        const mapMaxZoom = this._handleMapStyles({
            newMapCenter: mapParams.center
        });

        // Check if GL is supported
        if (!supported()) {
            AnalyticsService.trackWebGLError();

            if (this.alertPopup) {
                this.alertPopup.fire({
                    text: I18n.t('js.maps.errors.not_supported'),
                    icon: 'error'
                });
            }

            return false;
        }

        const pmtilesProtocol = new PMTilesProtocol();
        addProtocol('pmtiles', pmtilesProtocol.tile);

        this._model.map = new MapLibre({
            container: mapParams.mapId,
            style: this._model.mapStyle,
            minZoom: 3,
            maxZoom: mapMaxZoom,
            center: mapParams.center,
            bounds: mapParams.boundingBox,
            fitBoundsOptions: mapParams.boundingBox ? {padding: 80} : undefined,
            zoom: mapParams.zoom,
            attributionControl: false,
            // Force to use CTRL ou 2 fingers to move the map
            cooperativeGestures: mapParams.gestureEnabled || false,
            transformRequest: (url, resourceType) => {
                if (url?.includes('/v1/mapbox.mapbox-terrain-dem-v1/')) {
                    url = url.replace('https://a.tiles.mapbox.com/raster/v1/mapbox.mapbox-terrain-dem-v1', 'https://api.mapbox.com/raster/v1/mapbox.mapbox-terrain-dem-v1');
                    url = url.replace('.png?', '.webp?sku=101pwRKmRS10b&');
                } else if (isMapboxURL(url)) {
                    return transformMapboxUrl(url, resourceType, MapTileService.mapboxAccessToken);
                }

                return {
                    url: url
                };
            }
        });

        // Increase zoom speed on wheel for touchpad, (default 1 / 100)
        this._model.map.scrollZoom.setZoomRate(1 / 25);

        // Map scale
        if (!mapParams.hideMapScale) {
            this._model.map.addControl(new ScaleControl({
                unit: window.unit === 'mi' ? 'imperial' : 'metric'
            }), 'bottom-right');
        }

        // Add zoom and rotation controls buttons
        this._model.map.addControl(new NavigationControl({
            visualizePitch: true,
            showZoom: true,
            showCompass: true
        }));

        // Add a fullscreen control button
        this._model.map.addControl(new FullscreenControl());

        if (mapParams.geolocator) {
            // Add geolocate control to the map
            this._model.map.addControl(mapParams.geolocator);
        }

        // Add 3D view control button
        this._model.map.addControl(new Terrain3DControl({
            onClick: this._toggle3DTerrain,
            showProductModal: this._model.showProductModal
        }));

        // Set the map's max bounds and zoom for MapPlanner style
        this._manageMapProviderLimitation();

        // // Limit map usage
        // MapTileService.monitorMapUsage(this._model.map, mapParams.mapLimitations, this.toggleLoader);

        // Useless
        // mapInterface.map.addControl(
        //     new MapboxGeocoder({
        //         accessToken: maplibregl.accessToken,
        //         maplibregl: maplibregl,
        //         autocomplete: true,
        //         types: 'region,postcode,place,locality,address',
        //         proximity: {},
        //         countries: ''
        //     })
        // );

        return this._model.map;
    };

    _getCurrentMapPosition = () => {
        let mapCenter;
        let mapZoom ;
        let mapBoundingBox ;
        const userLocation = {
            lat: parseFloat(window.userLatitude) || 0,
            lng: parseFloat(window.userLongitude) || 0
        };
        let urlLocation = (new URL(window.location)).searchParams.get('location');

        if (urlLocation) {
            urlLocation = urlLocation.split(',');
            if (urlLocation[0] && urlLocation[1]) {
                mapCenter = {
                    lat: parseFloat(urlLocation[0]),
                    lng: parseFloat(urlLocation[1])
                };
            }

            if (urlLocation[2]) {
                mapZoom = parseInt(urlLocation[2]);
            }
        } else if (this._model.reusePosition) {
            const position = this._getSavedMapPosition();
            if (position && Utils.isPresent(position.center)) {
                mapCenter = position.center;
                mapZoom = position.zoom > this._model.searchZoom ? this._model.searchZoom : position.zoom;
            }
        } else if (Utils.isPresent(this._model.initialLocation)) {
            if (Array.isArray(this._model.initialLocation)) {
                if (Array.isArray(this._model.initialLocation[0]) && Array.isArray(this._model.initialLocation[1])) {
                    const boundingBox = new LngLatBounds([this._model.initialLocation[0][1], this._model.initialLocation[0][0]], [this._model.initialLocation[1][1], this._model.initialLocation[1][0]]);

                    mapBoundingBox = boundingBox;
                    mapCenter = boundingBox.getCenter();
                    mapZoom = null;
                } else {
                    this._model.initialLat = parseFloat(this._model.initialLocation[0]);
                    this._model.initialLong = parseFloat(this._model.initialLocation[1]);

                    if (this._model.initialLat && this._model.initialLong) {
                        mapCenter = {
                            lat: this._model.initialLat,
                            lng: this._model.initialLong
                        };
                        mapZoom = this._model.initialZoom || this._model.rideZoom;
                    }
                }
            } else if (this._model.initialLocation.center) {
                this._model.initialLat = parseFloat(this._model.initialLocation.center[1]);
                this._model.initialLong = parseFloat(this._model.initialLocation.center[0]);

                if (this._model.initialLat && this._model.initialLong) {
                    mapCenter = {
                        lat: this._model.initialLat,
                        lng: this._model.initialLong
                    };
                    mapZoom = this._model.initialZoom || this._model.rideZoom;
                }
            }
        } else if (Utils.isPresent(this._model.initialLat) && Utils.isPresent(this._model.initialLong)) {
            this._model.initialLat = parseFloat(this._model.initialLat);
            this._model.initialLong = parseFloat(this._model.initialLong);

            mapCenter = {
                lat: this._model.initialLat,
                lng: this._model.initialLong
            };
            mapZoom = this._model.initialZoom || this._model.rideZoom;
        }

        if (Utils.isPresent(mapBoundingBox)) {
            return {
                boundingBox: mapBoundingBox,
                center: mapCenter
            };
        } else if (Utils.isPresent(mapCenter) && Utils.isPresent(mapZoom)) {
            return {
                center: mapCenter,
                zoom: mapZoom
            };
        } else if (userLocation.lat !== 0 && userLocation.lng !== 0) {
            return {
                center: [userLocation.lng, userLocation.lat],
                zoom: MapInterface.userZoom
            };
        } else {
            return {
                center: window.mapLocation.reverse(),
                zoom: MapInterface.userZoom
            };
        }
    };

    _saveMapPosition = () => {
        if (!this._model.reusePosition) {
            return;
        }

        const mapCenter = this._model.map.getCenter();
        const mapZoom = this._model.map.getZoom();
        StorageService.setItem('MapCenter', mapCenter);
        StorageService.setItem('MapZoom', mapZoom);
    };

    _getSavedMapPosition = () => {
        if (!this._model.reusePosition) {
            return;
        }

        const center = StorageService.getItem('MapCenter');
        const zoom = StorageService.getItem('MapZoom');

        if (Utils.isPresent(center)) {
            return {
                center: center,
                zoom: zoom
            };
        }
    };

    _loadMarkerImages = (loadedCallback, images) => {
        Promise.all(
            images.map((image) => new Promise((resolve /*, reject */) => {
                if (this._model.map.hasImage(image.id)) {
                    resolve(image);
                } else {
                    this._model.map.loadImage(image.url)
                        .then((imageElement) => {
                            if (!this._model.map.hasImage(image.id)) {
                                this._model.map.addImage(image.id, imageElement.data);
                            }

                            resolve(image);
                        });
                }
            }))
        )
            .then((loadedImages) => {
                const sourceClusters = [];

                loadedImages.forEach((image) => {
                    const imageProps = image.properties || {};

                    if (this._model.map.getSource(image.id)) {
                        return;
                    }

                    // Add a data source containing one point feature
                    this._model.mapSources.push(image.id);
                    this._model.map.addSource(image.id, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: []
                        },
                        cluster: imageProps.cluster || false,
                        clusterMaxZoom: window.settings.disable_cluster_zoom || 14, // Max zoom to cluster points on
                        clusterRadius: window.settings.cluster_radius || 50 // Radius of each cluster when clustering points (defaults to 50)
                    });

                    // Add a layer to use the image to represent the data
                    this._model.mapLayers.push(image.id);
                    this._model.map.addLayer({
                        id: image.id,
                        type: 'symbol',
                        source: image.layerSourceId || image.id, // reference the data source
                        layout: {
                            // 'text-field': ['get', 'content'],
                            // 'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                            // 'text-radial-offset': 0.5,
                            // 'text-justify': 'auto',
                            'icon-image': image.id, // reference the image
                            'icon-size': typeof imageProps.size === 'undefined' ? 1 : imageProps.size,
                            'icon-offset': typeof imageProps.offset === 'undefined' ? [0, 0] : imageProps.offset,
                            'icon-anchor': imageProps.position || 'center',
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-rotate': imageProps.rotation || 0,
                            'icon-rotation-alignment': imageProps.rotationAlignment || 'auto',
                            // 'icon-ignore-placement': true
                            // 'symbol-placement': imageProps.placement || 'point'
                        },
                        paint: {
                            'icon-opacity': typeof imageProps.opacity === 'undefined' ? 1 : imageProps.opacity
                        }
                    });

                    if (imageProps.cluster) {
                        sourceClusters.push({
                            id: image.id,
                            imageProps: imageProps
                        });
                    }
                });

                // Display clusters above simple markers
                sourceClusters.forEach((image) => {
                    this._model.mapLayers.push(image.id + '-cluster');
                    this._model.map.addLayer({
                        id: image.id + '-cluster',
                        type: 'symbol',
                        source: image.id,
                        layout: {
                            'icon-image': 'cluster-marker', // reference the image
                            'icon-size': typeof image.imageProps.size === 'undefined' ? 1 : image.imageProps.size,
                            'icon-anchor': 'bottom',
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-offset': [0, 4],
                            'text-font': ['Roboto Regular'],
                            'text-size': 14,
                            'text-letter-spacing': 0.05,
                            'text-field': ['get', 'point_count'],
                            'text-offset': [0, -1.2] // move the label vertically downwards slightly to improve centering
                        },
                        paint: {
                            'text-color': 'black'
                        },
                        filter: ['>', 'point_count', 0]
                    });
                });
            })
            .then(() => {
                loadedCallback();
            });
    };

    _initializeSourcesAndLayers = (loadedCallback, initialParams = {}) => {
        // Load contours and hillshade as new source
        if (this._model.mapStyleName === 'map_planner_outdoor') {
            if (this._model.map.getSource('contours')) {
                return;
            }

            // Shifts hillshade and contours loading for performance reason
            setTimeout(() => {
                // this._updateHillshade({
                //     maxZoom: 18,
                //     visibility: 'visible'
                // });
                this._loadContours();
            }, 350);
        } else if (this._model.mapStyleName === 'map_planner') {
            if (this._model.map.getSource('contours')) {
                return;
            }

            // // Set hillshade available for everyone
            // // Shifts hillshade loading for performance reason
            // setTimeout(() => {
            //     this._updateHillshade({
            //         maxZoom: 18,
            //         visibility: 'visible'
            //     });
            // }, 300);
        } else {
            // this._model.map.removeLayer('hillshade');
            // this._model.map.removeLayer('contour_lines');
            // this._model.map.removeLayer('contour_labels');

            // if (this._model.map.getSource('hillshade')) {
            //     this._model.map.removeSource('hillshade');
            // }

            if (this._model.map.getSource('contours')) {
                this._model.map.removeSource('contours');
            }
        }

        this._updateLocaleMap();

        if (this._model.distanceUnits === 'mi') {
            this._updateUnitOnMap();
        }

        // this._updateHillshade({maxZoom: 2});
        // if (!this.isHillshadeAvailable()) {
        //     this._updateHillshade({maxZoom: 2});
        // }

        const defaultMarkers = [
            {
                id: 'arrow-marker',
                url: markerArrowIcon,
                properties: {
                    size: 0.4,
                    opacity: Utils.isPresent(initialParams.rideId) ? 0.5 : 0.85,
                    rotation: ['get', 'angle'],
                    rotationAlignment: 'map'
                }
            },
            {
                id: 'finish-marker',
                url: markerStopIcon,
                properties: {
                    size: 0.5
                }
            },
            {
                id: 'ride-finish-marker',
                url: markerStopIcon,
                properties: {
                    size: 0.5
                }
            },
            {
                id: 'bike-primary-marker',
                url: markerBikeIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'walk-primary-marker',
                url: markerWalkIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'running-primary-marker',
                url: markerRunningIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'hiking-primary-marker',
                url: markerHikingIcon,
                properties: {
                    size: 0.9,
                    cluster: this._model.clusterMode,
                    position: 'bottom',
                    offset: [0, 5]
                }
            },
            {
                id: 'cluster-marker',
                url: markerBlankIcon,
                properties: {
                    position: 'bottom'
                }
            },
            // {
            //     id: 'realtime-marker',
            //     url: `${window.settings.assets_url}/maps/realtime.png`,
            //     properties: {
            //         position: 'bottom',
            //         offset: [0, 15]
            //     }
            // }
        ];

        // Features are displayed by order of import: last import will be the top layer on map
        // Load first the less important elements
        this._loadMarkerImages(loadedCallback, [
            ...defaultMarkers,
            ...this._model.mapCustomMarkers
        ]);
    };

    _updateLocaleMap = () => {
        if (this._model.mapStyleName !== 'map_planner_outdoor' && this._model.mapStyleName !== 'map_planner') {
            return;
        }

        // Change locale names according to the current locale
        const translatedLabels = ['country_1', 'country_2', 'country_3', 'state', 'place_city', 'place_town', 'place_village', 'place_locality', 'place_other', 'parkname_large', 'parkname_small'];
        translatedLabels.forEach((label) => {
            this._model.map.setLayoutProperty(label, 'text-field', [
                'coalesce',
                ['get', `name:${I18n.locale}`],
                ['get', 'name:latin'],
            ]);
        });
    };

    // _updateHillshade = (options) => {
    //     if (!options || !this._model.map || !this._model.map.getSource('hillshade')) {
    //         return;
    //     }
    //
    //     if (options.maxZoom) {
    //         this._model.map.setLayerZoomRange('hillshade', 6, options.maxZoom);
    //     }
    //
    //     if (options.visibility) {
    //         this._model.map.setLayoutProperty('hillshade', 'visibility', options.visibility);
    //     }
    // };

    // _loadHillshade = (maxZoom = 24) => {
    //     this._model.map.addSource('hillshade', {
    //         'type': 'raster-dem',
    //         'url': 'https://hillshade.map-planner.com/data/hillshade_12.json'
    //     });
    //
    //     this._model.map.addLayer(
    //         {
    //             id: 'hillshade',
    //             source: 'hillshade',
    //             type: 'hillshade',
    //             minzoom: 6,
    //             maxzoom: maxZoom,
    //             paint: {
    //                 'hillshade-illumination-direction': 335,
    //                 'hillshade-highlight-color': 'rgba(189, 212, 192, 0.8)',
    //                 'hillshade-shadow-color': 'rgba(55, 61, 55, 0.8)',
    //                 'hillshade-accent-color': 'rgba(74, 86, 75, 0.9)',
    //                 'hillshade-exaggeration': [
    //                     'interpolate',
    //                     ['linear'],
    //                     ['zoom'],
    //                     4,
    //                     0.4,
    //                     7,
    //                     0.5,
    //                     10,
    //                     0.65,
    //                     11,
    //                     0.8,
    //                     12,
    //                     1
    //                 ],
    //                 'hillshade-illumination-anchor': 'viewport'
    //             }
    //         },
    //         // Insert hillshade after the following layer ID:
    //         'landcover_ice'
    //     );
    // };

    _loadContours = () => {
        if (!this._model.map) {
            return;
        }

        this._model.mapDemSource = new mlcontour.DemSource({
            url: 'https://hillshade.map-planner.com/data/{z}/{x}/{y}.webp',
            encoding: 'mapbox',
            maxzoom: 12,
            worker: true, // offload isoline computation to a web worker to reduce jank
            cacheSize: 300, // number of most-recent tiles to cache
            timeoutMs: 3_000 // timeout on fetch requests
        });

        this._model.mapDemSource.setupMaplibre({addProtocol});

        if (this._model.map.getSource('contours')) {
            return;
        }

        this._model.map.addSource('contours',
            {
                type: 'vector',
                tiles: [
                    this._model.mapDemSource.contourProtocolUrl({
                        // convert meters to feet, default=1 for meters
                        multiplier: this._model.distanceUnits === 'mi' ? 3.28084 : 1,
                        thresholds: {
                            // zoom: [minor, major]
                            // 11: [200, 1000],
                            12: [100, 500],
                            14: [50, 200],
                            15: [20, 100],
                            17: [20, 20]
                        },
                        // optional, override vector tile parameters:
                        contourLayer: 'contours',
                        elevationKey: 'ele',
                        levelKey: 'level',
                        extent: 4096,
                        buffer: 1
                    })
                ],
                maxzoom: 17
            });

        this._model.map.addLayer({
            id: 'contour_lines',
            type: 'line',
            source: 'contours',
            'source-layer': 'contours',
            minzoom: 12,
            paint: {
                'line-opacity': 0.8,
                'line-color': {
                    'stops': [
                        [13, 'rgba(114, 75, 21, 0.55)'],
                        [16, 'rgba(114, 75, 21, 0.9)']
                    ]
                },
                // level = highest index in thresholds array the elevation is a multiple of
                'line-width': ['match', ['get', 'level'], 1, .6, 0.3]
            }
        }, 'landuse_pitch');

        this._model.map.addLayer({
            id: 'contour_labels',
            type: 'symbol',
            source: 'contours',
            'source-layer': 'contours',
            minzoom: 14,
            filter: ['>', ['get', 'level'], 0],
            layout: {
                'symbol-placement': 'line',
                'text-size': 10,
                'text-field': ['concat', ['number-format', ['get', 'ele'], {}], this._model.distanceUnits === 'mi' ? 'ft' : 'm'],
                'text-font': ['Roboto Regular']
            },
            paint: {
                'text-color': 'rgba(114, 75, 21, 1)',
                'text-halo-color': 'rgba(255, 255, 255, 0.7)',
                'text-halo-width': 2,
                'text-halo-blur': 0.5
            }
        }, 'contour_lines');
    };

    _toggle3DTerrain = (is3D) => {
        if (!this._model.map) {
            return;
        }

        if (this._model.mapStyleName === 'mapbox_satellite') {
            if (is3D) {
                if (!this._model.map.getSource('mapbox-dem')) {
                    this._model.map.addSource('mapbox-dem', {
                        'type': 'raster-dem',
                        'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
                        'tileSize': 512,
                        'maxzoom': 14
                    });
                }

                this._model.map.setTerrain({
                    'source': 'mapbox-dem',
                    'exaggeration': 1
                });

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 80,
                    duration: 1000
                });
            } else {
                this._model.map.setTerrain();

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 0,
                    duration: 1000
                });
            }
        } else if (this._model.map.getSource('hillshade')) {
            if (is3D) {
                this._model.map.setTerrain({
                    'source': 'hillshade',
                    'exaggeration': 1
                });

                // Adjust camera to see in 3D
                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 80,
                    // bearing: 90,
                    duration: 1000,
                    // zoom: this._model.map.getZoom() - 1
                });
            } else {
                this._model.map.setTerrain();

                this._model.map.flyTo({
                    center: this._model.map.getCenter(),
                    pitch: 0,
                    duration: 1000
                });
            }
        } else {
            Notification.error(I18n.t('js.maps.3d.not_available'));
        }
    };

    _updateUnitOnMap = () => {
        // Change peak unit according to current map unit
        this._model.map.setLayoutProperty('poi_mountain_peak', 'text-field', [
            'concat',
            [
                'coalesce',
                ['get', 'name:latin'],
                ['get', 'name']
            ],
            ' ',
            ['get', this._model.distanceUnits === 'mi' ? 'ele_ft' : 'ele'],
            this._model.distanceUnits === 'mi' ? 'ft' : 'm'
        ]);
    };

    _convertPopupCoordinates = (event) => {
        const coordinates = event.features[0].geometry.coordinates.slice();

        // Ensure that if the map is zoomed out such that multiple copies of the feature are visible, the popup appears over the copy being pointed to.
        while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        return coordinates;
    };

    _manageCluster = (sourceName, pointEvent) => {
        const features = this._model.map.queryRenderedFeatures(pointEvent, {
            layers: [sourceName]
        });
        if (!features[0]) {
            return;
        }

        const clusterId = features[0].properties.cluster_id;

        this._model.map.getSource(sourceName)
            .getClusterExpansionZoom(clusterId)
            .then((zoom) => this.flyTo(features[0].geometry.coordinates, zoom + 1, 800));
    };

    _buildBounds = (bounds) => {
        if (bounds) {
            if (Array.isArray(bounds)) {
                return new LngLatBounds(
                    [parseFloat(bounds[0]), parseFloat(bounds[1])],
                    [parseFloat(bounds[2]), parseFloat(bounds[3])]
                );
            } else if (bounds._sw && bounds._ne) {
                return new LngLatBounds(bounds._sw, bounds._ne);
            } else {
                return new LngLatBounds();
            }
        } else {
            return new LngLatBounds();
        }
    };

    _formattedBounds = () => {
        return [[this._model.mapBounds._sw.lat, this._model.mapBounds._sw.lng], [this._model.mapBounds._ne.lat, this._model.mapBounds._ne.lng]];
    };
}
